/**
 * Unified Notification Service
 * Manages in-app notifications, bell badge, and auto-refresh
 * Works across all 10 dashboard types
 */

import { bridgeApi } from '@/lib/bridgeApi';

export interface AppNotification {
  id: string | number;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'booking' | 'inquiry' | 'confirmation';
  is_read: boolean;
  created_at: string;
  // Context
  source_type?: string; // member, patient, blood_bank, ambulance, homecare, healthcamp
  source_id?: number;
  action_url?: string;
  metadata?: Record<string, any>;
}

// Dashboard type config for notification fetching
export const DASHBOARD_CONFIGS: Record<string, { action: string; idField: string }> = {
  member: { action: 'member_notifications', idField: 'user_id' },
  patient: { action: 'member_notifications', idField: 'patient_id' },
  blood_bank: { action: 'member_notifications', idField: 'blood_bank_id' },
  ambulance: { action: 'member_notifications', idField: 'provider_id' },
  homecare: { action: 'member_notifications', idField: 'provider_id' },
  healthcamp: { action: 'member_notifications', idField: 'organizer_id' },
};

export interface FetchNotificationsOptions {
  limit?: number;
  page?: number;
  /** Bell dropdown passes true so a just-read item never reappears on the next poll. */
  unreadOnly?: boolean;
}

// Fetch notifications for any dashboard type
export const fetchNotifications = async (
  dashboardType: string,
  entityId: number | string,
  options: FetchNotificationsOptions = {}
): Promise<{ notifications: AppNotification[]; unread_count: number; total: number; page: number }> => {
  const { limit = 20, page = 1, unreadOnly = false } = options;
  const config = DASHBOARD_CONFIGS[dashboardType];
  if (!config) {
    return { notifications: [], unread_count: 0, total: 0, page: 1 };
  }

  try {
    const result = await bridgeApi.call<any>(config.action, {
      [config.idField]: entityId,
      dashboard_type: dashboardType,
      limit,
      page,
      unread_only: unreadOnly ? '1' : '0',
    });

    const notifications = (result?.notifications || result?.data || []) as AppNotification[];
    const unread_count = result?.unread_count ?? notifications.filter((n: AppNotification) => !n.is_read).length;
    const total = result?.total ?? notifications.length;

    return { notifications, unread_count, total, page };
  } catch (error) {
    console.warn(`[Notifications] Failed to fetch for ${dashboardType}:`, error);
    return { notifications: [], unread_count: 0, total: 0, page };
  }
};

// Mark notification as read. Throws on failure — callers must handle/surface the error,
// since silently swallowing it makes the bell badge look permanently "stuck" with no
// indication anything went wrong.
export const markNotificationRead = async (
  notificationId: string | number,
  dashboardType: string,
  entityId: number | string
): Promise<void> => {
  const config = DASHBOARD_CONFIGS[dashboardType];
  if (!config) throw new Error(`Unknown dashboard type: ${dashboardType}`);

  await bridgeApi.post('mark_notification_read', {
    notification_id: notificationId,
    dashboard_type: dashboardType,
    [config.idField]: entityId,
  });
};

// Mark all notifications as read. Throws on failure (see markNotificationRead).
export const markAllNotificationsRead = async (dashboardType: string, entityId: number | string): Promise<void> => {
  const config = DASHBOARD_CONFIGS[dashboardType];
  if (!config) throw new Error(`Unknown dashboard type: ${dashboardType}`);

  await bridgeApi.post('mark_all_notifications_read', {
    dashboard_type: dashboardType,
    [config.idField]: entityId,
  });
};

// Request browser push notification permission
export const requestPushPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) return false;

  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch {
    return false;
  }
};

// Show browser notification (fallback when push not available)
export const showBrowserNotification = (title: string, body: string, url?: string): void => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  const notification = new Notification(title, {
    body,
    icon: '/favicon-patakare-v2.png',
    badge: '/favicon-patakare-v2.png',
  } as NotificationOptions);

  if (url) {
    notification.onclick = () => {
      window.focus();
      window.location.href = url;
    };
  }
};
